/**
 * Runtime configuration.
 *
 * Read at boot from a script the server injects, not baked into the bundle. One built
 * artefact therefore serves every environment, which is what lets a tested image be
 * promoted rather than rebuilt per environment.
 *
 * Invalid configuration throws at boot. Degrading silently would mean the application
 * starts, looks fine, and then fails on whichever screen first touches the bad value.
 */

export const ENVIRONMENTS = [
  'local',
  'development',
  'test',
  'staging',
  'production',
] as const
export type Environment = (typeof ENVIRONMENTS)[number]

export const PROVIDER_IDS = ['github-copilot', 'codex', 'claude'] as const
export type ProviderId = (typeof PROVIDER_IDS)[number]

export const FEATURE_KEYS = [
  'claude',
  'auditExport',
  'notifications',
  'rriModule',
  'adkModule',
] as const
export type FeatureKey = (typeof FEATURE_KEYS)[number]

export interface PowerBiReport {
  readonly id: string
  readonly name: string
  readonly reportId: string
}

export interface AuthConfig {
  readonly tenantId: string
  readonly clientId: string
  readonly apiScope: string
  readonly allowDevIdentity: boolean
}

export interface PowerBiConfig {
  readonly tenantId: string
  readonly reports: readonly PowerBiReport[]
}

export interface RuntimeConfig {
  readonly apiBasePath: string
  readonly environment: Environment
  readonly auth: AuthConfig
  readonly powerBi: PowerBiConfig
  readonly providers: readonly ProviderId[]
  readonly features: Readonly<Record<FeatureKey, boolean>>
}

export class RuntimeConfigError extends Error {
  constructor(message: string) {
    super(`Runtime configuration is invalid: ${message}`)
    this.name = 'RuntimeConfigError'
  }
}

/**
 * Normalise the API base path and refuse anything that is not same-origin.
 *
 * A cross-origin API base would route credentials to another host. Refusing it here means
 * a configuration mistake cannot become a credential leak.
 */
function normaliseApiBasePath(value: unknown): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new RuntimeConfigError('apiBasePath is required')
  }
  const path = value.trim()
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(path) || path.startsWith('//')) {
    throw new RuntimeConfigError(
      `apiBasePath must be a same-origin path beginning with "/", received "${path}". ` +
        'A cross-origin base would send credentials to another host.',
    )
  }
  if (!path.startsWith('/')) {
    throw new RuntimeConfigError(
      `apiBasePath must begin with "/", received "${path}"`,
    )
  }
  return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path
}

function parseEnvironment(value: unknown): Environment {
  if (
    typeof value === 'string' &&
    (ENVIRONMENTS as readonly string[]).includes(value)
  ) {
    return value as Environment
  }
  throw new RuntimeConfigError(
    `environment must be one of ${ENVIRONMENTS.join(', ')}, received ${JSON.stringify(value)}`,
  )
}

function parseProviders(value: unknown): readonly ProviderId[] {
  if (!Array.isArray(value)) {
    throw new RuntimeConfigError('providers must be an array')
  }
  const unknown = value.filter(
    (id) => !(PROVIDER_IDS as readonly unknown[]).includes(id),
  )
  if (unknown.length > 0) {
    // Refused rather than ignored: an unrecognised provider means either a typo, which
    // would silently hide a governed provider, or a provider the interface cannot render.
    throw new RuntimeConfigError(
      `providers contains unknown ${unknown.length === 1 ? 'id' : 'ids'} ` +
        `${unknown.map((id) => JSON.stringify(id)).join(', ')}. ` +
        `Known ids are ${PROVIDER_IDS.join(', ')}.`,
    )
  }
  return value as readonly ProviderId[]
}

function parseFeatures(value: unknown): Readonly<Record<FeatureKey, boolean>> {
  const source = (value ?? {}) as Record<string, unknown>
  const unknown = Object.keys(source).filter(
    (key) => !(FEATURE_KEYS as readonly string[]).includes(key),
  )
  if (unknown.length > 0) {
    throw new RuntimeConfigError(
      `features contains unknown ${unknown.length === 1 ? 'key' : 'keys'} ` +
        `${unknown.map((key) => JSON.stringify(key)).join(', ')}. ` +
        `Known keys are ${FEATURE_KEYS.join(', ')}.`,
    )
  }
  const features = {} as Record<FeatureKey, boolean>
  for (const key of FEATURE_KEYS) {
    features[key] = source[key] === true
  }
  return features
}

function parseAuth(value: unknown, environment: Environment): AuthConfig {
  const source = (value ?? {}) as Record<string, unknown>
  const auth: AuthConfig = {
    tenantId: typeof source.tenantId === 'string' ? source.tenantId : '',
    clientId: typeof source.clientId === 'string' ? source.clientId : '',
    apiScope: typeof source.apiScope === 'string' ? source.apiScope : '',
    allowDevIdentity: source.allowDevIdentity === true,
  }

  // The same rule the API enforces, applied here so the two cannot disagree. A browser
  // offering a development identity against a deployed API would fail confusingly rather
  // than refusing clearly.
  if (auth.allowDevIdentity && environment !== 'local') {
    throw new RuntimeConfigError(
      `auth.allowDevIdentity is permitted only when environment is "local", received ` +
        `"${environment}". A development identity outside local is an authentication bypass.`,
    )
  }
  if (
    !auth.allowDevIdentity &&
    (auth.tenantId === '' || auth.clientId === '')
  ) {
    throw new RuntimeConfigError(
      'auth requires a tenantId and a clientId unless the local development identity is ' +
        'enabled. Refusing to run without an authentication boundary.',
    )
  }
  return auth
}

function parsePowerBi(value: unknown): PowerBiConfig {
  const source = (value ?? {}) as Record<string, unknown>
  const reports = Array.isArray(source.reports) ? source.reports : []
  return {
    tenantId: typeof source.tenantId === 'string' ? source.tenantId : '',
    reports: reports
      .filter(
        (report): report is Record<string, unknown> =>
          typeof report === 'object' && report !== null,
      )
      .map((report) => ({
        id: String(report.id ?? ''),
        name: String(report.name ?? ''),
        reportId: String(report.reportId ?? ''),
      }))
      // A report is kept when it is *named*, even with no identifier yet. Dropping the
      // unconfigured ones would hide from the reader that the report exists at all; keeping
      // it lets the screen say "this report is not configured in this environment", which
      // is the true statement and the actionable one.
      .filter((report) => report.id !== '' && report.name !== ''),
  }
}

export function createRuntimeConfig(source: unknown): RuntimeConfig {
  if (typeof source !== 'object' || source === null) {
    throw new RuntimeConfigError('no configuration object was injected')
  }
  const raw = source as Record<string, unknown>
  const environment = parseEnvironment(raw.environment)
  return {
    apiBasePath: normaliseApiBasePath(raw.apiBasePath),
    environment,
    auth: parseAuth(raw.auth, environment),
    powerBi: parsePowerBi(raw.powerBi),
    providers: parseProviders(raw.providers),
    features: parseFeatures(raw.features),
  }
}

let cached: RuntimeConfig | undefined

/** Read the injected configuration, validating once and caching the result. */
export function runtimeConfig(): RuntimeConfig {
  if (cached === undefined) {
    cached = createRuntimeConfig(window.__DEV_EX_CONFIG__)
  }
  return cached
}

/** Reset the cache. Test-only; production reads the configuration exactly once. */
export function resetRuntimeConfig(): void {
  cached = undefined
}
